"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.isBasicField = exports.computedFormattingToFormat = exports.isNullValue = exports.getMaxFieldCountPerSheet = exports.getMaxManageableSpaceCount = exports.getMaxViewCountPerSheet = exports.getApiMetaUserProperty = exports.getApiMetaPropertyFormat = exports.getViewTypeByString = exports.getViewTypeString = exports.getMemberTypeString = exports.getFieldTypeByString = exports.getFieldTypeString = exports.getEmojiIconNativeString = exports.groupArray = exports.handleEmptyCellValue = exports.handleNullArray = void 0;
const config_1 = require("../config");
const emojis_config_1 = require("../config/emojis_config");
const store_1 = require("../exports/store");
const types_1 = require("../types");
const field_api_enums_1 = require("./../types/field_api_enums");
const field_1 = require("./field");
const percent_field_1 = require("./field/percent_field");
/**
 *
 * Array null value processing, lookup's cv now contains null value by default, which will affect the original calculation logic.
 * In the lookup, the cellValue operation needs to be converted to a null value.
 * In the UI, the cellValue of the original rendered lookup component also needs to be processed
 * in this layer to ensure that it is consistent with the original display.
 * [null]=>null
 *
 */
const handleNullArray = (cv) => {
    if (cv == null) {
        return null;
    }
    if (Array.isArray(cv)) {
        cv = cv.filter(v => v != null);
    }
    if (cv.length === 0) {
        return null;
    }
    return cv;
};
exports.handleNullArray = handleNullArray;
/**
 * Semantic null value to null
 */
const handleEmptyCellValue = (cellValue, basicValueType) => {
    var _a;
    if (cellValue == null) {
        return null;
    }
    let cellValueType = basicValueType;
    if (!basicValueType) {
        if (Array.isArray(cellValue)) {
            cellValueType = types_1.BasicValueType.Array;
        }
        if (typeof cellValue === 'string') {
            cellValueType = types_1.BasicValueType.String;
        }
        if (typeof cellValue === 'boolean') {
            cellValueType = types_1.BasicValueType.Boolean;
        }
    }
    if (!cellValueType) {
        return cellValue;
    }
    switch (cellValueType) {
        case types_1.BasicValueType.Array:
            if (cellValue.length === 0) {
                return null;
            }
            break;
        case types_1.BasicValueType.Boolean:
            if (!cellValue) {
                return null;
            }
            break;
        case types_1.BasicValueType.String:
            if (cellValue.length === 0 ||
                (Array.isArray(cellValue) &&
                    cellValue.length === 1 &&
                    Object.prototype.toString.call(cellValue[0]) === '[object Object]' &&
                    !((_a = cellValue[0]) === null || _a === void 0 ? void 0 : _a.text))) {
                return null;
            }
            break;
        default:
            return cellValue;
    }
    return cellValue;
};
exports.handleEmptyCellValue = handleEmptyCellValue;
/**
 * [1,2,3,6,7,11,13,14] => [[1,2,3],[6,7],[11],[13,14]
 * @param list
 */
const groupArray = (list) => {
    const breaks = [0];
    list.forEach((currentRowIndex, index) => {
        const nextRowIndex = list[index + 1];
        if (nextRowIndex && nextRowIndex - currentRowIndex > 1) {
            breaks.push(index + 1);
        }
    });
    breaks.push(list.length);
    const res = [];
    while (breaks.length > 1) {
        res.push(list.slice(breaks[0], breaks[1]));
        breaks.shift();
    }
    return res;
};
exports.groupArray = groupArray;
/**
 * Convert the icon name stored in the emojiPicker to the string corresponding to the emoji.
 */
const getEmojiIconNativeString = (emoji) => {
    if (!emoji) {
        return emoji;
    }
    let emojiSlug = emoji;
    if (typeof emoji === 'object') {
        emojiSlug = emoji.id;
    }
    return emojis_config_1.EmojisConfig[emojiSlug].native;
};
exports.getEmojiIconNativeString = getEmojiIconNativeString;
const FieldTypeStringMap = {
    [types_1.FieldType.Attachment]: field_api_enums_1.APIMetaFieldType.Attachment,
    [types_1.FieldType.AutoNumber]: field_api_enums_1.APIMetaFieldType.AutoNumber,
    [types_1.FieldType.Checkbox]: field_api_enums_1.APIMetaFieldType.Checkbox,
    [types_1.FieldType.CreatedBy]: field_api_enums_1.APIMetaFieldType.CreatedBy,
    [types_1.FieldType.CreatedTime]: field_api_enums_1.APIMetaFieldType.CreatedTime,
    [types_1.FieldType.Currency]: field_api_enums_1.APIMetaFieldType.Currency,
    [types_1.FieldType.DateTime]: field_api_enums_1.APIMetaFieldType.DateTime,
    [types_1.FieldType.Email]: field_api_enums_1.APIMetaFieldType.Email,
    [types_1.FieldType.Formula]: field_api_enums_1.APIMetaFieldType.Formula,
    [types_1.FieldType.LastModifiedBy]: field_api_enums_1.APIMetaFieldType.LastModifiedBy,
    [types_1.FieldType.LastModifiedTime]: field_api_enums_1.APIMetaFieldType.LastModifiedTime,
    [types_1.FieldType.Link]: field_api_enums_1.APIMetaFieldType.MagicLink,
    [types_1.FieldType.LookUp]: field_api_enums_1.APIMetaFieldType.MagicLookUp,
    [types_1.FieldType.Member]: field_api_enums_1.APIMetaFieldType.Member,
    [types_1.FieldType.MultiSelect]: field_api_enums_1.APIMetaFieldType.MultiSelect,
    [types_1.FieldType.NotSupport]: field_api_enums_1.APIMetaFieldType.NotSupport,
    [types_1.FieldType.Number]: field_api_enums_1.APIMetaFieldType.Number,
    [types_1.FieldType.Percent]: field_api_enums_1.APIMetaFieldType.Percent,
    [types_1.FieldType.Phone]: field_api_enums_1.APIMetaFieldType.Phone,
    [types_1.FieldType.Rating]: field_api_enums_1.APIMetaFieldType.Rating,
    [types_1.FieldType.SingleSelect]: field_api_enums_1.APIMetaFieldType.SingleSelect,
    [types_1.FieldType.SingleText]: field_api_enums_1.APIMetaFieldType.SingleText,
    [types_1.FieldType.Text]: field_api_enums_1.APIMetaFieldType.Text,
    [types_1.FieldType.URL]: field_api_enums_1.APIMetaFieldType.URL,
    [types_1.FieldType.Cascader]: field_api_enums_1.APIMetaFieldType.Cascader,
};
const getFieldTypeString = (fieldType) => {
    return FieldTypeStringMap[fieldType];
};
exports.getFieldTypeString = getFieldTypeString;
const getFieldTypeByString = (fieldType) => {
    return Number(Object.keys(FieldTypeStringMap).find(key => FieldTypeStringMap[key] === fieldType));
};
exports.getFieldTypeByString = getFieldTypeByString;
const MemberTypeStringMap = {
    [types_1.MemberType.Member]: field_api_enums_1.APIMetaMemberType.Member,
    [types_1.MemberType.Team]: field_api_enums_1.APIMetaMemberType.Team,
};
const getMemberTypeString = (memberType) => {
    return MemberTypeStringMap[memberType];
};
exports.getMemberTypeString = getMemberTypeString;
const getViewTypeString = (viewType) => {
    const ViewTypeStringMap = {
        [store_1.ViewType.Grid]: field_api_enums_1.APIMetaViewType.Grid,
        [store_1.ViewType.Gallery]: field_api_enums_1.APIMetaViewType.Gallery,
        [store_1.ViewType.Kanban]: field_api_enums_1.APIMetaViewType.Kanban,
        [store_1.ViewType.Gantt]: field_api_enums_1.APIMetaViewType.Gantt,
        [store_1.ViewType.Calendar]: field_api_enums_1.APIMetaViewType.Calendar,
        [store_1.ViewType.OrgChart]: field_api_enums_1.APIMetaViewType.Architecture,
    };
    return ViewTypeStringMap[viewType];
};
exports.getViewTypeString = getViewTypeString;
const getViewTypeByString = (viewType) => {
    const viewTypeStringMap = {
        [field_api_enums_1.APIMetaViewType.Grid]: store_1.ViewType.Grid,
        [field_api_enums_1.APIMetaViewType.Gallery]: store_1.ViewType.Gallery,
        [field_api_enums_1.APIMetaViewType.Kanban]: store_1.ViewType.Kanban,
        [field_api_enums_1.APIMetaViewType.Gantt]: store_1.ViewType.Gantt,
        [field_api_enums_1.APIMetaViewType.Calendar]: store_1.ViewType.Calendar,
        [field_api_enums_1.APIMetaViewType.Architecture]: store_1.ViewType.OrgChart,
    };
    return viewTypeStringMap[viewType];
};
exports.getViewTypeByString = getViewTypeByString;
const getApiMetaPropertyFormat = (fieldInstance) => {
    // format to datetime
    if (types_1.BasicValueType.DateTime === fieldInstance.basicValueType) {
        if (!fieldInstance.field.property.formatting) {
            return {
                type: field_api_enums_1.APIMetaFieldPropertyFormatEnums.DateTime,
                format: {
                    dateFormat: field_1.DateTimeField.defaultDateFormat,
                    timeFormat: field_1.DateTimeField.defaultTimeFormat,
                    includeTime: false,
                },
            };
        }
        const { dateFormat, timeFormat, includeTime, timeZone, includeTimeZone } = (fieldInstance.field.property.formatting ||
            {});
        return {
            type: field_api_enums_1.APIMetaFieldPropertyFormatEnums.DateTime,
            format: {
                dateFormat: types_1.DateFormat[dateFormat],
                timeFormat: types_1.TimeFormat[timeFormat],
                includeTime,
                timeZone,
                includeTimeZone,
            },
        };
    }
    // format to number
    if (types_1.BasicValueType.Number === fieldInstance.basicValueType) {
        const formatting = fieldInstance.field.property.formatting;
        switch (formatting === null || formatting === void 0 ? void 0 : formatting.formatType) {
            case types_1.FieldType.Number:
                return {
                    type: field_api_enums_1.APIMetaFieldPropertyFormatEnums.Number,
                    format: {
                        precision: (formatting === null || formatting === void 0 ? void 0 : formatting.precision) || field_1.NumberField.defaultProperty().precision,
                    },
                };
            case types_1.FieldType.Percent:
                return {
                    type: field_api_enums_1.APIMetaFieldPropertyFormatEnums.Percent,
                    format: {
                        precision: (formatting === null || formatting === void 0 ? void 0 : formatting.precision) || percent_field_1.PercentField.defaultProperty().precision,
                    },
                };
            case types_1.FieldType.Currency:
                return {
                    type: field_api_enums_1.APIMetaFieldPropertyFormatEnums.Currency,
                    format: {
                        precision: (formatting === null || formatting === void 0 ? void 0 : formatting.precision) || field_1.CurrencyField.defaultProperty().precision,
                        symbol: (formatting === null || formatting === void 0 ? void 0 : formatting.symbol) || field_1.CurrencyField.defaultProperty().symbol,
                    },
                };
            default:
                return {
                    type: field_api_enums_1.APIMetaFieldPropertyFormatEnums.Number,
                    format: {
                        precision: (formatting === null || formatting === void 0 ? void 0 : formatting.precision) || field_1.NumberField.defaultProperty().precision,
                    },
                };
        }
    }
    return null;
};
exports.getApiMetaPropertyFormat = getApiMetaPropertyFormat;
const getApiMetaUserProperty = (uuids, userMap) => {
    let options = [];
    if (userMap) {
        options = uuids.reduce((pre, uuid) => {
            if (typeof uuid === 'string' && userMap[uuid]) {
                const { name, avatar } = userMap[uuid];
                pre.push({
                    id: uuid,
                    name,
                    avatar: avatar || '',
                });
            }
            return pre;
        }, []);
    }
    return {
        options,
    };
};
exports.getApiMetaUserProperty = getApiMetaUserProperty;
const getMaxViewCountPerSheet = () => {
    return (0, config_1.getCustomConfig)().MAXIMUM_VIEW_COUNT_PER_DATASHEET || 30;
};
exports.getMaxViewCountPerSheet = getMaxViewCountPerSheet;
const getMaxManageableSpaceCount = () => {
    return (0, config_1.getCustomConfig)().MAXIMUM_MANAGEABLE_SPACE_COUNT_PER_USER || 10;
};
exports.getMaxManageableSpaceCount = getMaxManageableSpaceCount;
const getMaxFieldCountPerSheet = () => {
    return (0, config_1.getCustomConfig)().MAXIMUM_FIELD_COUNT_PER_DATASHEET || 200;
};
exports.getMaxFieldCountPerSheet = getMaxFieldCountPerSheet;
/**
 * Check if value is empty, null, undefined, empty array[]
 * @param value
 */
const isNullValue = (value) => {
    return value == null || (Array.isArray(value) && value.length === 0);
};
exports.isNullValue = isNullValue;
/**
 * IOpenComputedFormat => IComputedFieldFormattingProperty
 * Convert the external read calculation field format content into executable cmd format (formula, magic reference)
 * @param fieldInstance
 * @param format
 */
const computedFormattingToFormat = (format) => {
    let formatting = undefined;
    switch (format === null || format === void 0 ? void 0 : format.type) {
        case field_api_enums_1.APIMetaFieldPropertyFormatEnums.DateTime:
            {
                const { dateFormat, timeFormat, includeTime } = format.format;
                formatting = {
                    dateFormat: types_1.DateFormat[dateFormat],
                    timeFormat: types_1.TimeFormat[timeFormat],
                    includeTime,
                };
            }
            break;
        case field_api_enums_1.APIMetaFieldPropertyFormatEnums.Currency:
            {
                const { precision, symbol } = format.format;
                formatting = {
                    formatType: types_1.FieldType.Currency,
                    precision,
                    symbol,
                };
            }
            break;
        case field_api_enums_1.APIMetaFieldPropertyFormatEnums.Number:
            {
                const { precision } = format.format;
                formatting = {
                    formatType: types_1.FieldType.Number,
                    precision,
                };
            }
            break;
        case field_api_enums_1.APIMetaFieldPropertyFormatEnums.Percent: {
            const { precision } = format.format;
            formatting = {
                formatType: types_1.FieldType.Percent,
                precision,
            };
        }
    }
    return formatting;
};
exports.computedFormattingToFormat = computedFormattingToFormat;
const isBasicField = (fieldType) => {
    return fieldType === types_1.FieldType.Text ||
        fieldType === types_1.FieldType.Number ||
        fieldType === types_1.FieldType.SingleSelect ||
        fieldType === types_1.FieldType.MultiSelect ||
        fieldType === types_1.FieldType.DateTime ||
        fieldType === types_1.FieldType.URL ||
        fieldType === types_1.FieldType.Email ||
        fieldType === types_1.FieldType.Phone ||
        fieldType === types_1.FieldType.Checkbox ||
        fieldType === types_1.FieldType.Rating ||
        fieldType === types_1.FieldType.Member ||
        fieldType === types_1.FieldType.Currency ||
        fieldType === types_1.FieldType.Percent ||
        fieldType === types_1.FieldType.SingleText ||
        fieldType === types_1.FieldType.Attachment;
};
exports.isBasicField = isBasicField;
//# sourceMappingURL=utils.js.map